/**
 * Utilities for handling product image variants
 */

// Product variant mappings based on the manifest
export const PRODUCT_VARIANTS = {
  'kbeacon': {
    variants: [
      '1-Black-KBeacon.webp',
      '2-Black-KBeacon.webp',
      '3-Black-KBeacon.webp',
      '4-Black-KBeacon.webp',
      '5-Black-KBeacon.webp',
      '6-Black-KBeacon.webp'
    ],
    primary: '1-Black-KBeacon.webp'
  },
  's6': {
    variants: [
      'S6.webp',
      'S6-2.webp',
      'S6-3.webp', 
      'S6-4.webp',
      'S6-5.webp'
    ],
    primary: 'S6.webp'
  },
  's6t2': {
    variants: [
      'S6T2.webp',
      'S6T2-2.webp'
    ],
    primary: 'S6T2-2.webp'
  },
  's6t3': {
    variants: [
      'S6T3.webp',
      'S6T3-2.webp'
    ],
    primary: 'S6T3-2.webp'
  },
  'gateway': {
    variants: [
      'gateway-1.webp',
      'gateway-2.webp',
      'gateway-3.webp',
      'gateway-4.webp'
    ],
    primary: 'gateway-4.webp'
  },
  'blogm': {
    variants: [
      'blogm.webp',
      'blogm2.webp'
    ],
    primary: 'blogm.webp'
  }
};

/**
 * Get all available WebP variants for a given product name
 */
export function getProductVariants(productName, imageUrl) {
  const name = productName.toLowerCase();
  
  // Check if product matches any variant group
  for (const [key, config] of Object.entries(PRODUCT_VARIANTS)) {
    if (name.includes(key)) {
      // For KBeacon, filter by color if specified
      let variants = config.variants;
      if (key === 'kbeacon') {
        if (name.includes('white')) {
          variants = variants.filter(v => v.includes('White'));
        } else if (name.includes('black')) {
          variants = variants.filter(v => v.includes('Black'));
        }
        // If no color specified, return all variants
      }
      
      return {
        variants: variants.map(variant => ({
          url: `/assets/products/${key}/${variant}`,
          name: variant.replace('.webp', '')
        })),
        hasMultiple: variants.length > 1
      };
    }
  }
  
  // Special handling for temperature & humidity sensors (KBeacon equivalents)
  if (name.includes('temperature') && name.includes('humidity') && name.includes('sensor')) {
    // Define all available KBeacon variants for temperature sensors
    const allKBeaconVariants = [
      '1-Black-KBeacon.webp',
      '2-Black-KBeacon.webp',
      '3-Black-KBeacon.webp',
      '4-Black-KBeacon.webp',
      '5-Black-KBeacon.webp',
      '6-Black-KBeacon.webp',
      '1-White-KBeacon.webp',
      '2-White-KBeacon.webp'
    ];
    
    let variants = allKBeaconVariants;
    if (name.includes('white') || name.endsWith('-white')) {
      variants = variants.filter(v => v.includes('White'));
    } else {
      // Black is the default when no white variant is specified
      variants = variants.filter(v => v.includes('Black'));
    }
    
    return {
      variants: variants.map(variant => ({
        url: `/assets/products/kbeacon/${variant}`,
        name: variant.replace('.webp', '')
      })),
      hasMultiple: variants.length > 1
    };
  }
  
  // Single image product
  return {
    variants: [{
      url: imageUrl,
      name: 'main'
    }],
    hasMultiple: false
  };
}

/**
 * Check if a product has multiple variants
 */
export function hasMultipleVariants(productName) {
  const name = productName.toLowerCase();
  for (const key of Object.keys(PRODUCT_VARIANTS)) {
    if (name.includes(key)) {
      return PRODUCT_VARIANTS[key].variants.length > 1;
    }
  }
  return false;
}

/**
 * Get the primary image for a product
 */
export function getPrimaryImage(productName, fallbackUrl) {
  const name = productName.toLowerCase();
  for (const [key, config] of Object.entries(PRODUCT_VARIANTS)) {
    if (name.includes(key)) {
      return `/assets/products/${key}/${config.primary}`;
    }
  }
  return fallbackUrl;
}
